
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight,
    FileText, UserCheck, ShieldAlert, Search, XCircle,
    AlertTriangle, FileCheck, Info, Filter, ArrowUp
} from 'lucide-react';
import API_BASE_URL from '../Config';
import { useAuth } from '../contexts/AuthContext';
import Swal from 'sweetalert2';

import { useLayout } from '../contexts/LayoutContext';

export default function BandejaAprobacionSocios() {
    const [socios, setSocios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterEstado, setFilterEstado] = useState('ALL');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [goToPage, setGoToPage] = useState('1');
    const { token } = useAuth();
    const navigate = useNavigate();

    const fetchPendientes = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/socio/pendientes`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!response.ok) throw new Error('Error al cargar socios');
            const data = await response.json();
            setSocios(data);
        } catch (error) {
            console.error(error);
            Swal.fire('Error', 'No se pudieron cargar las solicitudes', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPendientes();
    }, [token]);

    const handleAprobar = async (socio) => {
        if (socio.proponenteStatus === 'MORA') {
            Swal.fire('Bloqueado', 'El proponente está en MORA. No se puede aprobar.', 'error');
            return;
        }
        if (socio.isJunior && !socio.hasTutor) {
            Swal.fire('Bloqueado', 'Socio juvenil sin tutor vinculado.', 'error');
            return;
        }

        try {
            const result = await Swal.fire({
                title: `¿Aprobar a ${socio.nombre}?`,
                text: "Se activará el socio, se crearán sus cuentas y se generará la deuda de inscripción.",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#10B981',
                confirmButtonText: 'Sí, Aprobar y Activar',
                cancelButtonText: 'Cancelar'
            });

            if (result.isConfirmed) {
                Swal.fire({ title: 'Procesando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

                const response = await fetch(`${API_BASE_URL}/socio/${socio.id}/aprobar`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({ observacion: 'Aprobación desde grilla de admisión' })
                });

                if (!response.ok) {
                    let errorMessage = 'Error al procesar la aprobación.';
                    try {
                        const errorData = await response.json();
                        errorMessage = errorData.message || errorMessage;
                    } catch (e) {
                        const textError = await response.text();
                        if (textError) errorMessage = textError;
                    }
                    throw new Error(errorMessage);
                }

                Swal.fire('¡Éxito!', 'Socio activado correctamente.', 'success');
                fetchPendientes();
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'No se pudo Aprobar',
                text: error.message,
                confirmButtonColor: '#1e293b'
            });
        }
    };

    const handleRechazar = async (id) => {
        const { value: motivo } = await Swal.fire({
            title: 'Rechazar Solicitud',
            input: 'select',
            inputOptions: {
                'Documentación ilegible': 'Documentación ilegible',
                'Informconf Negativo': 'Informconf Negativo',
                'Proponente no cumple requisitos': 'Proponente no cumple requisitos',
                'Falta vinculación de tutor': 'Falta vinculación de tutor',
                'Otro': 'Otro (especificar)'
            },
            inputPlaceholder: 'Motivo del rechazo...',
            showCancelButton: true,
            confirmButtonText: 'Rechazar',
            cancelButtonText: 'Cancelar',
        });

        if (motivo) {
            try {
                const response = await fetch(`${API_BASE_URL}/socio/${id}/rechazar`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({ motivo })
                });

                if (!response.ok) throw new Error('Error al rechazar');

                Swal.fire('Rechazado', 'Solicitud rechazada.', 'info');
                fetchPendientes();
            } catch (error) {
                Swal.fire('Error', 'No se pudo rechazar', 'error');
            }
        }
    };

    const filteredSocios = socios.filter(s => {
        if (filterEstado === 'ALL') return true;
        if (filterEstado === 'SOLICITANTE') return s.estadoId === 4;
        if (filterEstado === 'PENDIENTE') return s.estadoId === 3;
        if (filterEstado === 'JUVENIL') return s.isJunior;
        if (filterEstado === 'ALERTA') return s.proponenteStatus === 'MORA' || (s.isJunior && !s.hasTutor);
        return true;
    });

    const sortedSocios = [...filteredSocios].sort((a, b) => {
        const dateA = new Date(a.fechaIngreso);
        const dateB = new Date(b.fechaIngreso);
        return dateA - dateB;
    });

    const totalPages = Math.ceil(sortedSocios.length / pageSize);
    const paginatedSocios = sortedSocios.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    useEffect(() => {
        setCurrentPage(1);
        setGoToPage('1');
    }, [filterEstado, pageSize]);

    const handlePageChange = (newPage) => {
        const page = Math.max(1, Math.min(newPage, totalPages));
        setCurrentPage(page);
        setGoToPage(page.toString());
    };

    const handleGoToPage = (e) => {
        if (e.key === 'Enter') {
            const page = parseInt(goToPage);
            if (!isNaN(page)) {
                handlePageChange(page);
            }
        }
    };

    const totalItems = sortedSocios.length;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, totalItems);

    return (
        <div className="p-6 bg-slate-50 min-h-screen">
            {/* Header & Filter */}
            <div className="mb-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900 mb-2">Bandeja de Admisión</h1>
                        <p className="text-slate-500 font-medium">Prioridad: Lo más antiguo primero • {filteredSocios.length} Solicitudes</p>
                    </div>

                    <div className="flex bg-white p-1 rounded-xl border border-slate-200 shadow-sm">
                        {[
                            { id: 'ALL', label: 'Todos' },
                            { id: 'SOLICITANTE', label: 'Sin Firma' },
                            { id: 'PENDIENTE', label: 'En Espera' },
                            { id: 'JUVENIL', label: 'Juveniles' },
                            { id: 'ALERTA', label: 'Con Alerta' }
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setFilterEstado(tab.id)}
                                className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${filterEstado === tab.id ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="py-32 flex flex-col items-center justify-center">
                    <div className="animate-spin h-8 w-8 border-[3px] border-blue-600 border-t-transparent rounded-full mb-4"></div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Cargando solicitudes...</p>
                </div>
            ) : (
                <div className="bg-white border border-slate-200 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-slate-200 bg-white">
                                    <th className="px-4 py-3 text-sm font-bold text-slate-700 w-20">Nro.</th>
                                    <th className="px-4 py-3 text-sm font-bold text-slate-700">Socio / Documento</th>
                                    <th className="px-4 py-3 text-sm font-bold text-slate-700">Estado</th>
                                    <th className="px-4 py-3 text-sm font-bold text-slate-700 text-center">Proponente</th>
                                    <th className="px-4 py-3 text-sm font-bold text-slate-700 text-center">Tipo</th>
                                    <th className="px-4 py-3 text-sm font-bold text-slate-700">Fecha</th>
                                    <th className="px-4 py-3 text-sm font-bold text-slate-700 text-center">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {paginatedSocios.map((socio) => {
                                    const isSolicitante = socio.estadoId === 4;
                                    const hasAlert = socio.proponenteStatus === 'MORA' || (socio.isJunior && !socio.hasTutor);

                                    return (
                                        <tr key={socio.id} className="hover:bg-slate-50 transition-colors">
                                            <td className="px-4 py-2 text-xs font-medium text-slate-400">
                                                #{socio.id}
                                            </td>
                                            <td className="px-4 py-2">
                                                <div className="flex items-center gap-2">
                                                    <div className="text-sm font-bold text-slate-900">{socio.nombre}</div>
                                                    {hasAlert && <AlertTriangle className="w-3.5 h-3.5 text-red-500" />}
                                                </div>
                                                <div className="text-[11px] text-slate-500">{socio.documento} • {socio.tipoPersona}</div>
                                            </td>
                                            <td className="px-4 py-2">
                                                <span className={`text-[10px] font-bold uppercase ${isSolicitante ? 'text-blue-600' : 'text-indigo-600'}`}>
                                                    {socio.estadoDescripcion}
                                                </span>
                                            </td>

                                            <td className="px-4 py-2 text-center">
                                                <div className="flex flex-col items-center">
                                                    <UserCheck className={`w-4 h-4 ${socio.proponenteStatus === 'APTO' ? 'text-emerald-500' : socio.proponenteStatus === 'MORA' ? 'text-red-500' : 'text-slate-300'}`} />
                                                    <span className="text-[9px] font-bold text-slate-400 mt-0.5">{socio.proponenteStatus}</span>
                                                </div>
                                            </td>

                                            <td className="px-4 py-2 text-center">
                                                <div className="text-[10px] font-bold text-slate-700 uppercase tracking-tight">
                                                    {socio.tipoSocioDescripcion}
                                                </div>
                                                {socio.isJunior && (
                                                    <div className={`text-[9px] font-bold ${socio.hasTutor ? 'text-blue-500' : 'text-amber-600'}`}>
                                                        {socio.hasTutor ? 'CON TUTOR' : 'SIN TUTOR'}
                                                    </div>
                                                )}
                                            </td>

                                            <td className="px-4 py-2 text-xs text-slate-500">
                                                {new Date(socio.fechaIngreso).toLocaleDateString()}
                                            </td>

                                            <td className="px-4 py-2">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        onClick={() => navigate(`/socios/detalle/${socio.id}`)}
                                                        className="px-3 py-1 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-all text-[10px] font-bold uppercase tracking-tight"
                                                    >
                                                        Ver Datos
                                                    </button>
                                                    
                                                    {isSolicitante ? (
                                                        <button
                                                            onClick={() => navigate(`/socios/${socio.id}/verificar-solicitud`)}
                                                            className="px-3 py-1 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-all text-[10px] font-bold uppercase tracking-tight"
                                                        >
                                                            Firma
                                                        </button>
                                                    ) : (
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => handleRechazar(socio.id)}
                                                                className="px-3 py-1 bg-rose-50 text-rose-600 rounded-lg hover:bg-rose-100 transition-all text-[10px] font-bold uppercase tracking-tight"
                                                            >
                                                                Rechazar
                                                            </button>
                                                            <button
                                                                onClick={() => handleAprobar(socio)}
                                                                className="px-3 py-1 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all text-[10px] font-bold uppercase tracking-tight"
                                                            >
                                                                Aprobar
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Paginación estilo Premium */}
                    <div className="p-4 bg-white border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500">Ir a</span>
                                <input 
                                    type="text" 
                                    className="w-12 h-8 border border-slate-200 rounded text-center text-xs font-bold outline-none focus:ring-2 focus:ring-blue-500"
                                    value={goToPage}
                                    onChange={(e) => setGoToPage(e.target.value)}
                                    onKeyDown={handleGoToPage}
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500">Ver</span>
                                <select 
                                    className="h-8 border border-slate-200 rounded text-xs font-bold px-2 outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                >
                                    {[5, 10, 20, 50, 100].map(val => (
                                        <option key={val} value={val}>{val}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="flex items-center gap-1">
                            <button 
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === 1 ? 'text-slate-300' : 'text-slate-600'}`}
                            >
                                <ChevronsLeft className="w-4 h-4" />
                            </button>
                            <button 
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === 1 ? 'text-slate-300' : 'text-slate-600'}`}
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>

                            <div className="flex items-center mx-2 gap-1">
                                {[...Array(totalPages)].map((_, i) => {
                                    const p = i + 1;
                                    if (totalPages > 5) {
                                        if (p !== 1 && p !== totalPages && Math.abs(p - currentPage) > 1) {
                                            if (p === 2 || p === totalPages - 1) return <span key={p} className="text-slate-300">...</span>;
                                            return null;
                                        }
                                    }
                                    return (
                                        <button
                                            key={p}
                                            onClick={() => handlePageChange(p)}
                                            className={`w-8 h-8 rounded text-xs font-bold transition-all ${currentPage === p ? 'bg-blue-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-100'}`}
                                        >
                                            {p}
                                        </button>
                                    );
                                })}
                            </div>

                            <button 
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === totalPages || totalPages === 0 ? 'text-slate-300' : 'text-slate-600'}`}
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                            <button 
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === totalPages || totalPages === 0 ? 'text-slate-300' : 'text-slate-600'}`}
                            >
                                <ChevronsRight className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                            [{totalItems > 0 ? startIndex + 1 : 0} a {endIndex} de {totalItems}]
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
